import { getRemovedFilesIds } from '@risksmart-app/components/src/file/fileUtils';
import type { UseFileUpdateOptions } from '@risksmart-app/components/src/file/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '../utils/trpc';
import { useFileUploadTRPC } from './useFileUploadTRPC';

/**
 * tRPC twin of `@risksmart-app/components/src/file/useFileUpdate` (V2: REST
 * upload + Apollo deleteRelationFileById), scoped to the single-parent path the
 * portal uses. Same removed-file diffing, same option shape, same
 * `{ updateFiles, error }` return. Mirrors
 * `packages/web/src/hooks/mutations/file/useFileUpdateTRPC.tsx`.
 *
 * Consumers must import the flag-gated `useFileUpdate` wrapper, never this.
 */
export const useFileUpdateTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const uploadFiles = useFileUploadTRPC();
  const deleteRelationFilesMutation = useMutation({
    ...trpc.frontend.files.deleteRelationFiles.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.files.getById.queryKey(),
        });
      },
    }),
  });

  return {
    updateFiles: async ({
      parentType,
      parentId,
      originalFiles,
      selectedFiles,
    }: UseFileUpdateOptions): Promise<void> => {
      const parentIds = [parentId];
      const removedFileIds = getRemovedFilesIds(
        originalFiles?.map((f) => ({
          Id: f != null && !(f instanceof File) ? f.Id : undefined,
        })) ?? [],
        selectedFiles?.filter(
          (f): f is { Id: string | undefined } =>
            f != null && !(f instanceof File)
        ) || []
      );

      await uploadFiles({
        parentType,
        parentIds,
        newFiles: selectedFiles?.filter((f): f is File => f instanceof File),
      });

      if (removedFileIds.length > 0) {
        await deleteRelationFilesMutation.mutateAsync({
          ParentIds: parentIds,
          FileIds: removedFileIds,
        });
      }
    },
    error: '',
  };
};
